// @flow
import request from 'superagent'
import type { IdentifyType } from '../reducers/identify'
import type { ExtentType, ViewType } from '../reducers/map'
import type { UserMapsourceType } from '../reducers/mapsources'
import * as types from '../constants/action_types'
import { appendToLogger } from './logger'

export type OrderingType = {
    mapsourceName: string,
    layerName: string,
}

function getLayerFromMapsource(getState, mapsourceName, layerName) {
    const mapsources = getState().mapsources.mapsources
    const imapsource = mapsources.findIndex(f => f.name === mapsourceName)
    const mapsource = mapsources[imapsource]
    // Locate the layer
    //   First try by filepath. Search by name should go away.
    let layer = mapsource.layers.find(l => l.filepath === layerName)
    if (!layer) {
        layer = mapsource.layers.find(l => l.name === layerName)
    }
    return layer
}

export const changeBaseLayer = (baseLayer: string) => ({ type: types.CHANGE_BASELAYER, baseLayer })
export const mapsourceIsLoading = (mapsource: UserMapsourceType, isLoading) => ({ type: types.USER_MAPSOURCE_LOADING, mapsourceName: mapsource.name, isLoading })
export const onChangeMapScale = (mapScale: number) => ({ type: types.CHANGE_MAPSCALE, mapScale })
export const setMapExtent = (extent: ExtentType) => ({ type: types.SET_MAP_EXTENT, extent })
export const setUserLayers = (mapsources: Array<UserMapsourceType>) => ({ type: types.USER_LAYERS_SET, mapsources })
export const setUserLayersSort = (mapsourceName: string, layerNames: string[]) =>
    ({ type: types.USER_LAYERS_SORT, mapsourceName, layerNames })
export const showMapToolbar = (show: boolean) => ({ type: types.SHOW_MAP_TOOLBAR, show })
export const updateMapView = (mapView: ViewType) => ({ type: types.UPDATE_MAP_VIEW, mapView })
export const updateMapsourceLayers = (mapsourceName: string, layerNames: string, newProps: {}) =>
    ({ type: types.UPDATE_MAPSOURCE_LAYERS, mapsourceName, layerNames, newProps })
export const updateServiceProps = (siteName: string, serviceName: string, props: {}) => ({ type: types.PUBLIC_DATA_PROPS_CHANGE, siteName, serviceName, serviceProps: props })
export const updateUserLayer = (mapsourceName: string, layerName: string, newProps: {}) =>
    ({ type: types.USER_LAYER_UPDATE, mapsourceName, layerName, newProps })
export const mapExtentUpdateHandled = () => ({ type: types.SET_MAP_EXTENT_HANDLED })
export const mapViewUpdateHandled = () => ({ type: types.SET_MAP_VIEW_HANDLED })

export const getUserLayerClassifications = (mapsourceName: string, layerName: string, fieldName: string, classifyType: string, kwargs: {}) => (dispatch: Function, getState: Function) => {
    const token = getState().token.value
    fetch(`/er2_map/get_layer_classifications/?token=${token}`,
        {
            body: JSON.stringify({ mapsource_name: mapsourceName, layer: layerName, field: fieldName, classify_type: classifyType, ...kwargs }),
            method: 'POST',
        })
        .then(res => res.json())
        .then(res => kwargs.success(res.classifications))
        .catch(err => res => console.error(`Got failure to update layers: ${res}`))
}

export const onChangeBaseLayer = (baseLayer: string) => (dispatch: Function, getState: Function) => {
    // Only update state if the state has been initialized
    const state = getState()
    if (state.serverState.isFetched) {
        const token = getState().token.value
        fetch(`/er2_map/api/v1/state/map/?token=${token}`,
            {
                body: JSON.stringify({ baseLayer }),
                method: 'PUT',
            })
        dispatch(changeBaseLayer(baseLayer))
    }
}

export const onUpdateMapsourceOrdering = (mapsourceNames: string[]) => (dispatch: Function, getState: Function) => {
    const token = getState().token.value
    fetch(`/er2_map/update_mapsource_ordering/?token=${token}`,
        {
            body: JSON.stringify({ mapsource_names: mapsourceNames }),
            method: 'POST',
        })
}

export const onUpdateGroupLayerOrdering = (ordering: OrderingType[], mapsourceNames?: string[]) => (dispatch: Function, getState: Function) => {
    const token = getState().token.value
    fetch(`/er2_map/update_group_layer_ordering/?token=${token}`,
        {
            body: JSON.stringify({ ordering }),
            method: 'POST',
        })
        .then(res => res.json())
        .then((res) => {
            dispatch(setUserLayers(res.state.mapsources))
            if (mapsourceNames !== undefined) {
                dispatch(onUpdateMapsourceOrdering(mapsourceNames))
            }
        })
        .catch(err => res => console.error(`Got failure to update mapfile ordering: ${res}`))
}

export const onUpdateMapView = (mapView: ViewType) => (dispatch: Function, getState: Function) => {
    // Only update state if the state has been initialized
    const state = getState()
    if (state.serverState.isFetched) {
        // Don't save if the mapView isn't valid
        if (mapView.mapScale) {
            const token = getState().token.value
            fetch(`/er2_map/api/v1/state/map/?token=${token}`,
                {
                    body: JSON.stringify({ mapView }),
                    method: 'PUT',
                })
        }
    }
    dispatch(updateMapView(mapView))
}

export const onUpdateUserLayers = () => (dispatch: Function, getState: Function) => {
    const state = getState()
    const token = getState().token.value
    fetch(`/er2_map/api/v1/state/map/?token=${token}`, { method: 'POST' })
        .then(res => res.json())
        .then(json => dispatch(setUserLayers(json.state.mapsources)))
}

export const onUserLayerDelete = (mapsourceName: string, layerName: string) => (dispatch: Function, getState: Function) => {
    const token = getState().token.value
    fetch(`/er2_map/delete_map_layer/?token=${token}`,
        {
            body: JSON.stringify({ mapsource_name: mapsourceName, layer: layerName }),
            method: 'POST',
        })
        .then(res => dispatch({ type: types.USER_LAYER_DELETE, mapsourceName, layerName }))
        .catch(err => res => dispatch(appendToLogger([`Got failure to update layers: ${res}`])))
}

export const onUserLayerDownload = (mapsourceName: string, layerName: string) => (dispatch: Function, getState: Function) => {
    const token = getState().token.value
    window.open(`/er2_map/download_map_layer/?token=${token}&mapsource_name=${mapsourceName}&layer=${encodeURIComponent(layerName)}&foo=${Math.random()}`)
}

export const onUserLayersZoom = (mapsourceName: string, layerNames: string[]) => (dispatch: Function, getState: Function) => {
    const layer = getLayerFromMapsource(getState, mapsourceName, layerNames[0])
    dispatch(setMapExtent(layer.bbox_3857))
}

export const onUserLayersPropsChange = (mapsourceName: string, layerNames: string | string[], newprops: {}) =>
    (dispatch: Function, getState: Function) => {
        const token = getState().token.value
        if (!Array.isArray(layerNames)) layerNames = [layerNames]
        if (layerNames.length) {
            fetch(`/er2_map/update_map_layers/?token=${token}`,
                {
                    body: JSON.stringify({ mapsource_name: mapsourceName, layers: layerNames, newprops }),
                    method: 'POST',
                })
                .then(res => res.json())
                .then(res => dispatch(setUserLayers(res.state.mapsources)))
                .catch(err => res => console.warn(`Got failure to update layers: ${res}`))
        }
    }

export const onUserLayersAdd = (mapsourceName: string, acceptedFiles: []) => (dispatch: Function, getState: Function) => {
    const token = getState().token.value
    const req = request.post(`/er2_map/add_map_layers/?token=${token}&mapsource_name=${mapsourceName}`)
    acceptedFiles.forEach(file => req.attach(file.name, file))
    req.then(resp => {
        dispatch(onUpdateUserLayers())
    }).catch(ret => {
        dispatch(appendToLogger([ret.response.text]))
    })
}
